/**
 * `format` — the one object every v2 type carries and no widget read (CT-5641).
 *
 * Nine of the twenty-nine types publish a `format` beside their numbers, and
 * the contract is the same object every time: a `style` plus the handful of
 * options that style takes. Before this, each widget printed its own numbers
 * — `toLocaleString()` here, a bare `${value}` there, a hand-rolled `%` in a
 * third — so the same 0.84 read as `0.84`, `84%` and `0.8` depending on which
 * drawing you were looking at.
 *
 * The backend sends the shape, not the string: `{ style: 'CURRENCY',
 * currency_code: 'USD' }` and `120000`, never `"$120,000"`. That is deliberate
 * — a widget that receives a formatted string cannot right-align it, cannot
 * compare it, and cannot re-render it in another locale. So the formatting
 * lives here, once.
 *
 * ## Absent format is not an error
 *
 * Most values arrive with none, and a number with no stated style is a number:
 * it prints with the locale's own grouping and nothing else. Inventing a style
 * — guessing currency from a column called `amount` — is how a count of
 * invoices ends up with a dollar sign.
 */

/**
 * The locale every widget formats in.
 *
 * English, explicitly, rather than `undefined` — which means "whatever the
 * reader's browser is set to". The product is English today: the labels are
 * English, the samples are English, and `NotesListWidget` already hardcodes
 * `en-US` for the same reason. Following the browser instead produced a card
 * whose labels said «Sales» and whose axis said «ago», which is neither
 * language.
 *
 * This is the one place to change when the widgets are wired to the app's
 * selected language. Everything locale-dependent in the catalogue — numbers,
 * currencies, dates, axis ticks — goes through here, so that change is a
 * parameter, not a sweep.
 */
export const WIDGET_LOCALE = 'en-US';

/** `Intl` options per style, before the per-call overrides. */
const BASE_OPTIONS = Object.freeze({
  INTEGER: { maximumFractionDigits: 0 },
  DECIMAL: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  PERCENT: { style: 'percent' },
  CURRENCY: { style: 'currency' },
});

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);

/**
 * `3h 25m` from seconds.
 *
 * Deliberately not `Intl.DurationFormat`: it is not in every runtime we ship
 * to, and a duration on a KPI is read at a glance rather than parsed.
 */
const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

/** `2026-10-07`, with nothing after it. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A date out of the contract, as a `Date` the reader's calendar agrees with.
 *
 * A date-only string is built as a **local** date rather than handed to
 * `new Date()`. `new Date('2021-03-02')` is parsed as UTC midnight, and every
 * `toLocale*` call then renders it in the reader's own zone — which in the
 * Americas is the evening before. The contract's `due_date`, `since`,
 * `period.start` and every `x` on a TIME axis are date-only, so without this
 * they all read a day early for most of the hemisphere: a bar chart of
 * `2026-08-01`, `2026-09-01`, `2026-10-01` labelled Jul / Aug / Sep.
 *
 * A timestamp keeps the normal parse: it names an instant, and showing that
 * instant in the reader's zone is the correct thing to do.
 *
 * Returns `null` when the value is not a date at all, so a caller can fall
 * back to printing it verbatim rather than showing `Invalid Date`.
 */
export function parseContractDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const formatDate = (value, withTime) => {
  const date = parseContractDate(value);
  if (!date) return String(value);
  return withTime ? date.toLocaleString(WIDGET_LOCALE) : date.toLocaleDateString(WIDGET_LOCALE);
};

/**
 * A value, printed the way its `format` says.
 *
 * @param {number|string|boolean|null|undefined} value
 * @param {object} [format] The type's own `format` object: `style`, and
 *   `prefix` / `suffix` / `compact` / `decimals` / `currency_code` /
 *   `date_pattern` as that style takes them.
 * @param {string} [emptyText='—'] What an absent value reads as. A dash, not an
 *   empty cell: a blank reads as "the widget failed", a dash as "there is none".
 * @returns {string}
 */
export function formatValue(value, format = null, emptyText = '—') {
  if (value === null || value === undefined || value === '') return emptyText;

  const style = format?.style;
  const prefix = format?.prefix ?? '';
  const suffix = format?.suffix ?? '';

  /*
    Dates are checked before the string branch, not after.

    Every date in the contract arrives as a string — `2026-10-07T09:00:00Z`,
    `2021-03-02` — so a `typeof value === 'string'` test placed first swallows
    every one of them and prints the raw ISO text. That is not a hypothetical:
    it is what `created_at`, `due_date`, `since`, `generated_at` and every
    TIMESTAMP column looked like until this order was fixed.
  */
  if (style === 'DATE' || style === 'DATETIME') {
    return `${prefix}${formatDate(value, style === 'DATETIME')}${suffix}`;
  }
  if (style === 'TEXT' || typeof value === 'string') return `${prefix}${value}${suffix}`;
  if (style === 'BOOLEAN' || typeof value === 'boolean') {
    return `${prefix}${value ? 'Yes' : 'No'}${suffix}`;
  }
  if (!isNumber(value)) return `${prefix}${value}${suffix}`;
  if (style === 'DURATION') return `${prefix}${formatDuration(value)}${suffix}`;

  const options = { ...(BASE_OPTIONS[style] ?? {}) };
  if (style === 'CURRENCY') options.currency = format?.currency_code || 'USD';
  if (format?.compact) {
    options.notation = 'compact';
    options.maximumFractionDigits = format?.decimals ?? 1;
  }
  if (typeof format?.decimals === 'number') {
    options.minimumFractionDigits = format.decimals;
    options.maximumFractionDigits = format.decimals;
  }

  try {
    return `${prefix}${new Intl.NumberFormat(WIDGET_LOCALE, options).format(value)}${suffix}`;
  } catch {
    // An unknown currency code is the usual cause, and a number the reader can
    // still use beats an exception that blanks the whole widget.
    return `${prefix}${value.toLocaleString(WIDGET_LOCALE)}${suffix}`;
  }
}

/**
 * A ratio printed as a signed percentage — `+20.0%`, `−3.4%`.
 *
 * `delta_pct`, `target_pct`, `pct` and `share` all arrive as ratios (0.2, not
 * 20), and every one of them is read as a percentage. The sign is explicit
 * because a delta without one is ambiguous at a glance, and it uses a real
 * minus sign rather than a hyphen so the two line up in a column.
 *
 * @param {number|null|undefined} ratio
 * @param {object} [options]
 * @param {boolean} [options.signed=true] Prefix `+` on a positive value.
 * @param {number} [options.decimals=1]
 */
export function formatRatio(ratio, { signed = true, decimals = 1 } = {}) {
  if (!isNumber(ratio)) return '—';
  const text = `${Math.abs(ratio * 100).toFixed(decimals)}%`;
  if (ratio < 0) return `−${text}`;
  return signed ? `+${text}` : text;
}

/** A ratio as a 0–100 bar length, clamped. `null` when there is nothing to draw. */
export function ratioToPercent(ratio) {
  if (!isNumber(ratio)) return null;
  return Math.min(100, Math.max(0, ratio * 100));
}

export default formatValue;
